// Agent Service - Communication with AI Agent Edge Function
import { supabase } from '../../lib/supabase';
import type {
  AgentContext,
  ToolCallResult,
  Conversation,
} from '../types/agent';
import { logger } from '../../lib/logger';

export interface AIUsageInfo {
  daily_count: number;
  monthly_count: number;
  daily_limit: number;
  monthly_limit: number;
  daily_remaining: number;
  monthly_remaining: number;
  daily_exceeded: boolean;
  monthly_exceeded: boolean;
}

export class RateLimitError extends Error {
  usage: AIUsageInfo;
  constructor(message: string, usage: AIUsageInfo) {
    super(message);
    this.name = 'RateLimitError';
    this.usage = usage;
  }
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Options for sending a message to the agent
export interface SendMessageOptions {
  conversationId?: string;
  message: string;
  context: AgentContext;
  onContentDelta: (delta: string) => void;
  onToolCallStart: (toolCallId: string, name: string) => void;
  onToolResult: (toolCallId: string, name: string, result: ToolCallResult) => void;
  onMessageStart: (data: { conversationId: string }) => void;
  onMessageEnd: () => void;
  onError: (error: string) => void;
}

/**
 * Send a message to the AI agent with streaming response
 */
export async function sendAgentMessage(options: SendMessageOptions): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Nicht authentifiziert');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/ai-agent`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      conversationId: options.conversationId,
      message: options.message,
      context: options.context,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 429 && errorData.usage) {
      throw new RateLimitError(
        errorData.message || 'Anfragelimit erreicht',
        errorData.usage as AIUsageInfo
      );
    }
    throw new Error(errorData.error || `Agent-Anfrage fehlgeschlagen: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Keine Antwort vom Server');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim()) continue;

        const eventMatch = line.match(/^event: (.+)$/m);
        const dataMatch = line.match(/^data: (.+)$/m);

        if (eventMatch && dataMatch) {
          const event = eventMatch[1];
          let data;

          try {
            data = JSON.parse(dataMatch[1]);
          } catch {
            logger.error('Failed to parse SSE data:', dataMatch[1]);
            continue;
          }

          switch (event) {
            case 'message_start':
              options.onMessageStart(data);
              break;
            case 'content_delta':
              options.onContentDelta(data.delta);
              break;
            case 'tool_call_start':
              options.onToolCallStart(data.toolCallId, data.name);
              break;
            case 'tool_result':
              options.onToolResult(data.toolCallId, data.name, data.result);
              break;
            case 'message_end':
              options.onMessageEnd();
              break;
            case 'error':
              options.onError(data.error);
              break;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Get user's conversations
 */
export async function getConversations(limit = 20): Promise<Conversation[]> {
  const { data, error } = await supabase
    .from('agent_conversations')
    .select('id, title, updated_at')
    .is('deleted_at', null)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data || []).map((conv) => ({
    id: conv.id,
    title: conv.title,
    updatedAt: conv.updated_at,
  }));
}

/**
 * Get messages for a conversation
 */
export async function getConversationMessages(conversationId: string) {
  const { data, error } = await supabase
    .from('agent_messages')
    .select('id, role, content, tool_calls, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Soft delete a conversation
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  const { error } = await supabase
    .from('agent_conversations')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', conversationId);

  if (error) throw error;
}

/**
 * Get current AI usage stats for the authenticated user
 */
export async function getAIUsage(): Promise<AIUsageInfo | null> {
  const { data, error } = await supabase.rpc('get_ai_usage', {
    p_user_id: (await supabase.auth.getUser()).data.user?.id,
  });
  if (error) {
    logger.warn('Failed to fetch AI usage:', error);
    return null;
  }
  return data as AIUsageInfo;
}

/**
 * Log a client-side action
 */
export async function logClientAction(
  conversationId: string | null,
  actionType: string,
  actionInput: Record<string, unknown>,
  success: boolean,
  error?: string
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return;

  await supabase.from('agent_action_logs').insert({
    user_id: user.id,
    conversation_id: conversationId,
    action_type: actionType,
    action_input: actionInput,
    action_output: { success },
    status: success ? 'success' : 'failed',
    error_message: error || null,
    execution_location: 'client',
  });
}
